import { useState, useMemo, useEffect } from 'react';
import ProductsHero from './components/ProductsHero';
import ProductCard from './components/ProductCard';
import ProductModal from './components/ProductModal';
import { products, productCategories } from '@/mocks/products';

type Product = typeof products[0];

const PRODUCTS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/products#webpage`,
  url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/products`,
  name: 'MAX-Sourcing | Global Sourcing of ingredients & Fine Chemicals',
  description:
    'MAX-Sourcing delivers global sourcing of fine chemicals, APIs, and compliant supply chain solutions. Serving clients across US, Europe, Canada, China and India.',
  inLanguage: 'en',
  isPartOf: {
    '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/#website`,
  },
  dateModified: '2026-04-07',
  translationOfWork: {
    '@type': 'CollectionPage',
    '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/produits#webpage`,
    url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/produits`,
    inLanguage: 'fr',
  },
  workTranslation: {
    '@type': 'CollectionPage',
    '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/produits#webpage`,
    url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/produits`,
    inLanguage: 'fr',
  },
  mainEntity: {
    '@type': 'ItemList',
    name: 'MAX-Sourcing Product Catalogue',
    description: 'Fine chemicals, pharmaceutical APIs, food ingredients, industrial and mining chemicals.',
    numberOfItems: products.length,
    itemListElement: products.slice(0, 20).map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: p.name,
      description: `${p.grade} grade ${p.category} chemical — CAS: ${p.casNumber ?? 'N/A'}`,
    })),
  },
};

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'MAX-Sourcing | Global Sourcing of ingredients & Fine Chemicals';

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'MAX-Sourcing delivers global sourcing of fine chemicals, APIs, and compliant supply chain solutions. Serving clients across US, Europe, Canada, China and India.');
    setMeta('keywords', 'pharmaceutical sourcing, fine chemicals, API sourcing, cGMP, global supply chain, pharmaceutical APIs, chemical procurement');

    const id = 'schema-products';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(PRODUCTS_SCHEMA);
    return () => {
      document.title = prevTitle;
      document.getElementById(id)?.remove();
    };
  }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchCat = activeCategory === 'All' || p.category === activeCategory;
      const matchSearch =
        search.trim() === '' ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.grade.toLowerCase().includes(search.toLowerCase()) ||
        p.category.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [activeCategory, search]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <main className="flex-1">
        <ProductsHero />

        {/* Filter bar */}
        <div className="w-full bg-white border-b border-stone-100 sticky top-20 z-30">
          <div className="w-full px-6 md:px-16 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Search */}
            <div className="relative w-full sm:w-72 shrink-0">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
              />
            </div>

            {/* Category pills */}
            <div className="flex flex-wrap gap-2">
              {productCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                    activeCategory === cat
                      ? 'bg-emerald-700 text-white'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Products grid */}
        <div className="w-full px-6 md:px-16 py-12">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <i className="ri-search-line text-4xl mb-3 block" />
              <p className="text-base font-medium">No products found</p>
              <p className="text-sm mt-1">Try adjusting your search or category filter</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-stone-400 font-semibold uppercase tracking-widest mb-6">
                {filtered.length} product{filtered.length !== 1 ? 's' : ''} found
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filtered.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onSelect={setSelectedProduct}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Enquiry CTA */}
        <div className="w-full bg-emerald-700 px-6 md:px-16 py-14 text-center">
          <h2
            className="text-3xl font-extrabold text-white mb-3"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Don&apos;t see what you need?
          </h2>
          <p className="text-emerald-100 text-base mb-7 max-w-xl mx-auto">
            We source a wide range of products, ingredients and fine chemicals beyond this list. If you don't see what you are looking for, contact us with your requirements.
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-emerald-700 font-bold text-sm px-8 py-3 rounded-md hover:bg-emerald-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Send an Enquiry
          </a>
        </div>
      </main>

      {selectedProduct && (
        <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}
    </div>
  );
}
